/** Base (no sale), host-app aware: fullPrice + override(delta). */
export function getBasePrice(
  product: any,
  hostApplication?: { id?: string } | null,
): number {
  if (!product) return 0;
  const fallback =
    typeof product?.fullPrice === 'number' ? product.fullPrice : 0;

  const hostId = hostApplication?.id;
  if (!hostId) return fallback;

  const match = product?.supportedHostApplications?.find(
    (a: any) => a.id === hostId,
  );
  const delta =
    typeof match?.priceOverride === 'number' &&
    !Number.isNaN(match.priceOverride)
      ? match.priceOverride
      : 0;

  return Math.max(0, fallback + delta);
}

/** Sale-aware unit price, host-app aware: min(full+delta, sale+delta) when sale is active. */
export function getEffectivePrice(product: any, hostApplication?: any): number {
  const { sale } = product ?? {};
  const fallback =
    typeof product?.fullPrice === 'number' ? product.fullPrice : 0;

  const hostId = hostApplication?.id;
  const match = hostId
    ? product?.supportedHostApplications?.find((a: any) => a.id === hostId)
    : null;
  const delta =
    typeof match?.priceOverride === 'number' &&
    !Number.isNaN(match.priceOverride)
      ? match.priceOverride
      : 0;

  const baseWithDelta = Math.max(0, fallback + delta);

  const now = Date.now();
  const saleActive =
    sale &&
    typeof sale.salePrice === 'number' &&
    (!sale.until || new Date(sale.until).getTime() > now) &&
    sale.salePrice < baseWithDelta;

  const saleWithDelta = saleActive ? Math.max(0, sale.salePrice + delta) : null;

  return saleWithDelta != null ? saleWithDelta : baseWithDelta;
}

/** Discount from list price, then choose the lower of sale-aware vs discounted list (never stack).
 *
 * Dollars: full amount off each unit (all categories).
 * Percent: full percent off each unit.
 */
export function getDiscountedPrice(
  product: any,
  discount: any,
  hostApplication?: any,
): number {
  const saleAware = getEffectivePrice(product, hostApplication);
  if (!discount) return saleAware;

  const list = getBasePrice(product, hostApplication);

  let discountedFromList = list;
  if (discount.amountUnits === 'percent') {
    discountedFromList = list * (1 - Number(discount.amount) / 100);
  } else if (discount.amountUnits === 'dollars') {
    discountedFromList = list - (Number(discount.amount) || 0);
  }

  return Math.max(0, Math.min(saleAware, discountedFromList));
}

/** True if a sale lowers the host-app base price right now. */
export function isOnSale(
  product: any,
  _discount: any,
  hostApplication?: any,
): boolean {
  const base = getBasePrice(product, hostApplication);
  const effective = getEffectivePrice(product, hostApplication);
  return (
    effective < base && new Date(product?.sale?.until).getTime() > Date.now()
  );
}
